import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
    signInWithPopup, 
    GoogleAuthProvider, 
    onAuthStateChanged, 
    signOut,
    User as FirebaseUser 
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { saveAvatar, loadAvatar } from '../services/avatarStorageService';
import { getBetaCodePlan } from '../services/betaCodeService';

interface User {
    id: string;
    name: string;
    email: string;
    plan: 'free' | 'pro' | 'enterprise';
    avatar?: string;
    betaCode?: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: () => Promise<boolean>;
    loginWithGoogle: () => Promise<boolean>;
    signup: (name: string, email: string, password: string, betaCode?: string) => Promise<boolean>;
    logout: () => void;
    updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Sync with Firebase Auth state
    useEffect(() => {
        if (!auth) {
            setIsLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser) {
                // Restore saved avatar from IndexedDB if available
                const savedAvatar = await loadAvatar();
                const betaCodePlan = getBetaCodePlan();

                const newUser: User = {
                    id: firebaseUser.uid,
                    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
                    email: firebaseUser.email || '',
                    plan: betaCodePlan || 'pro',
                    avatar: savedAvatar || firebaseUser.photoURL || DEFAULT_AVATAR,
                };
                setUser(newUser);
                
                // Store UID for AppContext sync if needed
                localStorage.setItem('way2will_user_id', firebaseUser.uid);
            } else {
                setUser(null);
                localStorage.removeItem('way2will_user_id');
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Save avatar to IndexedDB when it changes
    useEffect(() => {
        if (user?.avatar && !user.avatar.startsWith('http')) {
            saveAvatar(user.avatar).catch(err => {
                console.error('[Auth] Failed to save avatar to IndexedDB:', err);
            });
        }
    }, [user?.avatar]);

    const loginWithGoogle = async (): Promise<boolean> => {
        if (!auth) {
            console.error('[Auth] Firebase Auth not initialized');
            return false;
        }

        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            return true;
        } catch (error) {
            console.error('[Auth] Google Login Error:', error);
            return false;
        }
    };

    // Keep login and signup for backward compatibility, but they both use Google Auth now
    const login = async (): Promise<boolean> => {
        return loginWithGoogle();
    };

    const signup = async (_name: string, _email: string, _password: string, _betaCode?: string): Promise<boolean> => {
        return loginWithGoogle();
    };

    const logout = async () => {
        if (auth) {
            try {
                await signOut(auth);
            } catch (error) {
                console.error('[Auth] Logout Error:', error);
            }
        }
        setUser(null);
    };

    const updateUser = (updates: Partial<User>) => {
        if (user) {
            setUser({ ...user, ...updates });
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated: !!user,
                login,
                loginWithGoogle,
                signup,
                logout,
                updateUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
